import { isDefined, isNotDefined } from '@sgme/fp';
import {
  BatchFormFieldType,
  buildSingleCellId,
  switchRadioButtons,
  updateFieldValueOnDrag,
} from 'components/batchForm/BatchForm.helpers';
import { CellMetaData } from 'components/common-form/builder/form/node/field/Grid/GridField.model';
import { BatchDragAndDropContext } from 'components/common-form/contexts/batch/BatchDragAndDropContext';
import { BatchNavigationContextValue } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { BatchPasteDataContextValue } from 'components/common-form/contexts/batch/BatchPasteDataContext';
import type { FieldMetadata, FieldType } from 'models/forms/fields';
import type { RadioButtonsFieldMetadata } from 'models/forms/fields/RadioButtonsFieldMetadata';
import { DragEvent, FocusEvent, MouseEvent, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { FormStatus } from 'state/forms/models';

import { useBatchFormNavLayerState } from './useBatchFormNavLayerState';

interface UseCellInteractionsProps {
  rowIndex: number;
  columnIndex: number;
  value: BatchFormFieldType | undefined;
  setValue: (value: BatchFormFieldType | undefined) => void;
  type: string;
  metadata?: FieldMetadata;
  formStatus?: FormStatus;
  canEdit: boolean;
  setLostInputBeforeEditing: (input: string | undefined) => void;
  navigationContext: BatchNavigationContextValue;
  batchPasteDataContext: BatchPasteDataContextValue;
}

export const useCellInteractions = ({
  rowIndex,
  columnIndex,
  value,
  setValue,
  type,
  metadata,
  formStatus,
  canEdit,
  setLostInputBeforeEditing,
  navigationContext,
  batchPasteDataContext,
}: UseCellInteractionsProps) => {
  const { isCellEditable, isCellDraggable, isCellSelectable, isFormPricing, isCellHidden } = useBatchFormNavLayerState({
    metadata,
    formStatus,
  });

  const { setSelectedCell, setIsEditing, addGridRef } = navigationContext;

  const { setPastedData } = batchPasteDataContext;

  const dragDropContext = useContext(BatchDragAndDropContext);
  const {
    dragHandleCellId,
    dragHoveredCellIds,
    draggedCellIdsForCopy,
    onDragStart,
    onDragOver,
    onDrop,
    setOriginalDraggedValue,
    originalDraggedValue,
    setDragHandleCellId,
    setDragHoveredCellIds,
    setDraggedCellIdsForCopy,
  } = dragDropContext;

  const cellId = useMemo(() => buildSingleCellId(rowIndex, columnIndex), [rowIndex, columnIndex]);
  const gridCellRef = useRef<HTMLDivElement>(null);

  const isDragHandledDisplayed = useMemo(
    () => metadata?.withoutDragHandle !== true && cellId === dragHandleCellId && isCellEditable,
    [dragHandleCellId, isCellEditable, cellId, metadata],
  );

  const isDragHoveringCell = useMemo(() => {
    return dragHoveredCellIds?.includes(cellId);
  }, [dragHoveredCellIds, cellId]);

  // Drag handlers
  const handleDragStart = useCallback(
    (e: DragEvent) => {
      if (!isDefined(onDragStart) || !isDefined(setOriginalDraggedValue)) {
        return;
      }
      setOriginalDraggedValue(value);
      onDragStart(e, rowIndex, columnIndex);
    },
    [onDragStart, rowIndex, columnIndex, value, setOriginalDraggedValue],
  );

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      if (!isDefined(onDragOver) || isFormPricing) {
        return;
      }
      onDragOver(e, rowIndex, columnIndex);
    },
    [onDragOver, rowIndex, columnIndex, isFormPricing],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      if (!isDefined(onDrop)) {
        return;
      }
      onDrop(e, rowIndex);
    },
    [onDrop, rowIndex],
  );

  // Mouse handlers
  const onMouseEnter = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      if (!isCellDraggable || isNotDefined(setDragHandleCellId)) {
        return;
      }
      setDragHandleCellId(cellId);
    },
    [cellId, isCellDraggable, setDragHandleCellId],
  );

  const onMouseLeave = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      if (isNotDefined(setDragHandleCellId)) {
        return;
      }
      setDragHandleCellId(undefined);
    },
    [setDragHandleCellId],
  );

  // Click handlers
  const onClick = useCallback(
    (e: MouseEvent) => {
      setDragHoveredCellIds?.(undefined);
      setDraggedCellIdsForCopy?.(undefined);
      if (gridCellRef.current !== null && setSelectedCell && isCellSelectable) {
        setSelectedCell({ row: rowIndex, column: columnIndex });
      }

      if (type === 'RadioButtonsField' && value !== null && isCellEditable) {
        e.preventDefault();
        const target = e.target as HTMLLabelElement;
        const radioButtonsFieldMetadata: RadioButtonsFieldMetadata = metadata as RadioButtonsFieldMetadata;
        switchRadioButtons(radioButtonsFieldMetadata.availableItems, value as string, setValue, Number(target.id));
      }
    },
    [
      setDragHoveredCellIds,
      setDraggedCellIdsForCopy,
      setSelectedCell,
      isCellSelectable,
      rowIndex,
      columnIndex,
      type,
      value,
      isCellEditable,
      metadata,
      setValue,
    ],
  );

  const onDoubleClick = useCallback(() => {
    if (canEdit && setIsEditing) {
      setIsEditing(true);
    }
  }, [canEdit, setIsEditing]);

  const onBlur = useCallback(
    (e: FocusEvent) => {
      if (setIsEditing && !e.currentTarget.contains(e.relatedTarget)) {
        setIsEditing(false);
        setLostInputBeforeEditing(undefined);
        setPastedData?.(undefined);
      }
    },
    [setIsEditing, setLostInputBeforeEditing, setPastedData],
  );

  // Effects
  useEffect(() => {
    if (addGridRef && gridCellRef.current !== null) {
      addGridRef(cellId, { el: gridCellRef.current, type, handleDrop } as CellMetaData);
    }
  });

  useEffect(() => {
    if (!draggedCellIdsForCopy?.includes(cellId) || !isCellEditable || isCellHidden) {
      return;
    }
    updateFieldValueOnDrag(type as FieldType, value, originalDraggedValue, setValue);
  }, [cellId, originalDraggedValue, value, type, setValue, isCellEditable, isCellHidden, draggedCellIdsForCopy]);

  return {
    cellId,
    gridCellRef,
    isDragHandledDisplayed,
    isDragHoveringCell,
    handleDragStart,
    handleDragOver,
    handleDrop,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onDoubleClick,
    onBlur,
    setDragHoveredCellIds,
    setDraggedCellIdsForCopy,
  };
};
